import React, { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { GameDto } from '../../../models/game/dto/game.dto';
import { useUpdateGameTimes } from '../hooks/useUpdateGameTimes';

const toDatetimeLocalString = (date: Date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

interface EditGameTimesModalProps {
    isOpen: boolean;
    onClose: () => void;
    game: GameDto;
}

export const EditGameTimesModal: React.FC<EditGameTimesModalProps> = ({
    isOpen,
    onClose,
    game,
}) => {
    const updateGameTimesMutation = useUpdateGameTimes();
    const [startTime, setStartTime] = useState(
        toDatetimeLocalString(new Date(game.startTime))
    );
    const [duration, setDuration] = useState(
        Math.round(game.duration / (60 * 1000)) // Convert from milliseconds to minutes
    );

    const handleSubmit = async () => {
        try {
            const startTimeDate = new Date(startTime);
            const durationMs = duration * 60 * 1000; // Convert minutes to milliseconds

            await updateGameTimesMutation.mutateAsync({
                gameId: game.id,
                data: {
                    startTime: startTimeDate.toISOString(),
                    duration: durationMs,
                },
            });
            onClose();
        } catch (error) {
            // Error is handled by the hook
        }
    };

    const handleClose = () => {
        // Reset form to original values
        setStartTime(toDatetimeLocalString(new Date(game.startTime)));
        setDuration(Math.round(game.duration / (60 * 1000)));
        onClose();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={translate('game.edit_times')}
            onSubmit={handleSubmit}
            submitLabel={translate('game.update_times')}
            cancelLabel={translate('game.cancel_edit')}
            isSubmitting={updateGameTimesMutation.isLoading}
        >
            <FormStack>
                <FormField
                    type="datetime-local"
                    label={translate('game.start_time')}
                    value={startTime}
                    onChange={(value) => setStartTime(value)}
                    isRequired
                    min={toDatetimeLocalString(new Date())}
                />

                <FormField
                    type="number"
                    label={`${translate('game.duration')} (${translate('game.minutes')})`}
                    value={duration}
                    onChange={(value) => setDuration(value)}
                    isRequired
                    min={15}
                    max={300}
                    step={15}
                />
            </FormStack>
        </FormModal>
    );
};
